import type { IstoreItem } from '@typings/store/storeItem.interface';

// 미션 타입 정의
export enum QuestType {
  MAIN = 'main',
  SUB = 'sub',
  MEMBER_REQUEST = 'member_request',
  USER = 'user',
}

export enum QuestStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum RewardType {
  ITEM = 'item', // 특정 아이템 지급 (복수 선택 가능)
  STAT = 'stat', // 특정 스탯 수치 올리기 (ex: mp+1)
  CURRENCY = 'currency', // 재화 지급
  TITLE = 'title', // 타이틀 지급
  EXPERIENCE = 'experience', // (하위 호환용) 경험치 - STAT으로 대체 권장
}

// 스탯 보상 상세 정보
export interface IStatReward {
  statId: string; // 스탯 ID
  amount: number; // 올릴 수치 (양수: 증가, 음수: 감소)
}

// 퀘스트 보상 인터페이스
export interface IQuestReward {
  type: RewardType;
  name?: string; // 보상 이름 (선택)
  amount?: number; // 수량 (재화, 아이템 개수 등)
  icon?: string; // 아이콘 (선택)
  // 아이템 보상
  itemId?: string; // 단일 아이템 ID (하위호환용)
  itemIds?: string[]; // 다수 아이템 ID 배열
  itemData?: Partial<IstoreItem>; // StoreItem 스냅샷(이미지/제목/설명 등)
  // 스탯 보상
  statRewards?: IStatReward[]; // 스탯 보상 목록 (복수 스탯 가능)
  // 타이틀 보상
  titleId?: string; // 타이틀 ID
  description?: string;
}

// 퀘스트 요청자 정보 인터페이스
export interface IQuestRequester {
  key: string; // 캐릭터 ID
  path: string; // 컬렉션 경로 (photocard 또는 photocard2)
  thumbnail?: string; // 캐릭터 썸네일
  value?: string; // 캐릭터 이름
}

// 퀘스트 참여자 상태
export enum QuestMemberStatus {
  ACCEPTED = 'accepted', // 수락 (진행 중)
  ABANDONED = 'abandoned', // 포기
  COMPLETED = 'completed', // 완료
}

export const QuestMemberStatusMap: Record<QuestMemberStatus, string> = {
  [QuestMemberStatus.ACCEPTED]: '진행 중',
  [QuestMemberStatus.ABANDONED]: '포기',
  [QuestMemberStatus.COMPLETED]: '완료',
};

// 퀘스트 참여자(수락한 멤버) 정보 인터페이스
export interface IQuestParticipant {
  odId: string; // 유저 UID (통합 필드)
  characterId?: string; // 캐릭터 ID (선택)
  // 상태 관리
  status?: QuestMemberStatus; // 상태 (기본: ACCEPTED)
  joinedAt: number; // 수락 시간 (timestamp)
  updatedAt?: number; // 마지막 업데이트 시간 (timestamp)
  note?: string; // 관리자 메모
}

// 레거시 데이터 호환을 위한 유틸리티 함수
export function getParticipantId(p: IQuestParticipant | { userId?: string; odId?: string }): string {
  return (p as any).odId || (p as any).userId || '';
}

// 퀘스트 기본 정보 인터페이스
export interface IQuest {
  id: string;
  type: QuestType;
  title: string;
  subTitle?: string;
  summary?: string;
  description: string;
  location?: string;
  requirements?: string[];
  rewards: IQuestReward[];
  difficulty: number; // 1-5 (별점)
  priority: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // 관리자 또는 사용자 ID
  isActive: boolean;
  bgImgURL?: string; // 사용자 설정 배경 이미지 URL
  hoverBgImgURL?: string; // 사용자 설정 호버 배경 이미지 URL
  borderImgURL?: string; // 사용자 설정 보더 이미지 URL
  iconURL?: string; // 퀘스트 아이콘 이미지 URL (정사각형 권장)
  isShowTitle?: boolean; // 타이틀 표시 여부
  acceptCondition?: IQuestAcceptCondition; // 퀘스트 수락(진행) 조건
  completionCondition?: IQuestCompletionCondition; // 레거시 단일 완료 조건 (하위 호환)
  completionConditions?: IQuestCompletionCondition[]; // 복수 완료 조건 (AND 로직)
  requestedBy?: IQuestRequester; // 퀘스트 요청자 (캐릭터 정보)
  participants?: IQuestParticipant[]; // 퀘스트를 수락한 멤버 목록
  isRandomReward?: boolean; // 랜덤 보상 지급 여부 (true: 보상 중 하나만 랜덤 지급)
  repeatSchedule?: IQuestRepeatSchedule; // 반복 조건 (요일 반복)
}

// 사용자별 퀘스트 상태 인터페이스
export interface IUserQuest {
  id: string;
  userId: string;
  missionId: string;
  status: QuestStatus;
  startedAt?: Date;
  completedAt?: Date;
  cancelledAt?: Date;
  progress?: number; // 0-100 진행률
  notes?: string; // 사용자 메모
}

// 퀘스트 로그 댓글 인터페이스
export interface IQuestComment {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: Date;
  updatedAt?: Date;
  isDeleted?: boolean;
}

// 퀘스트 로그 좋아요 인터페이스
export interface IQuestLogLike {
  id: string;
  logId: string;
  userId: string;
  createdAt: Date;
}

// 퀘스트 필터 타입
export type QuestFilterType = 'all' | QuestType;

// 로그 타임라인 타입
export type LogTimelineType = 'all' | 'mission';

// 퀘스트 정렬 옵션
export enum QuestSortOption {
  PRIORITY = 'priority',
  CREATED_DATE = 'created_date',
  DIFFICULTY = 'difficulty',
  STATUS = 'status',
}

// 퀘스트 검색 필터
export interface IQuestSearchFilter {
  type?: QuestFilterType;
  status?: QuestStatus;
  difficulty?: number;
  sortBy?: QuestSortOption;
  sortOrder?: 'asc' | 'desc';
  searchText?: string;
}

// 퀘스트 생성/수정을 위한 입력 타입
export interface IQuestInput {
  type: QuestType;
  title: string;
  subTitle?: string;
  description: string;
  location?: string;
  requirements?: string[];
  rewards: Omit<IQuestReward, 'itemId'>[];
  difficulty: number;
  priority: number;
}

// 퀘스트 로그 생성을 위한 입력 타입
export interface IQuestLogInput {
  missionId: string;
  content: string;
  images?: File[]; // 업로드할 이미지 파일들
  hashtags?: string[];
  location?: string;
}

// 퀘스트 액션 타입
export enum QuestActionType {
  ACCEPT = 'accept',
  CANCEL = 'cancel',
  COMPLETE = 'complete',
  RESTART = 'restart',
}

// 퀘스트 완료 조건 타입
export enum QuestCompletionType {
  ITEM_OWNERSHIP = 'item_ownership', // 특정 아이템 보유 시
  STAT_VALUE = 'stat_value', // 특정 스탯 수치 보유 시
  LOG_COUNT = 'log_count', // 특정 로그 갯수 업로드 완료 시
}

// 퀘스트 수락(진행) 조건 타입
export enum QuestAcceptConditionType {
  NONE = 'none', // 조건 없음
  STAT = 'stat', // 스탯 수치 조건
  ITEM = 'item', // 아이템 보유 조건
}

// 퀘스트 수락 스탯 조건 인터페이스
export interface IQuestAcceptStatCondition {
  statId: string; // 스탯 ID
  minValue: number; // 최소 요구 수치
}

// 퀘스트 수락 아이템 조건 인터페이스
export interface IQuestAcceptItemCondition {
  itemId: string; // 아이템 ID
  quantity: number; // 필요 수량
}

// 퀘스트 수락(진행) 조건 인터페이스
export interface IQuestAcceptCondition {
  type: QuestAcceptConditionType;
  // 스탯 조건 (type === 'stat' 일 때)
  statConditions?: IQuestAcceptStatCondition[];
  // 아이템 조건 (type === 'item' 일 때)
  itemConditions?: IQuestAcceptItemCondition[];
  // 조건 미충족 시 표시할 메시지
  failMessage?: string;
}

// 로그 랜덤 보상 아이템 인터페이스
export interface ILogRandomReward {
  id: string; // 고유 ID
  message: string; // 출력할 지문 메시지
  itemId?: string; // 지급할 아이템 ID (선택)
  itemAmount?: number; // 지급할 아이템 수량 (기본 1)
}

// 퀘스트 완료 조건 인터페이스
export interface IQuestCompletionCondition {
  type: QuestCompletionType;
  // 아이템 보유 조건
  itemIds?: string[]; // 보유해야 할 아이템 ID 목록
  itemAmounts?: Record<string, number>; // 아이템별 필요 수량 (기본 1)
  // 스탯 수치 조건
  statConditions?: {
    statId: string; // 스탯 ID
    minValue: number; // 최소 필요 수치
  }[];
  // 로그 갯수 조건
  logCount?: number; // 업로드해야 할 로그 갯수
  // 로그 업로드 시 랜덤 보상 설정 (LOG_COUNT 타입에서만 사용)
  logRandomRewards?: ILogRandomReward[]; // 랜덤 보상 목록
  enableLogRandomReward?: boolean; // 랜덤 보상 활성화 여부
}

export interface IQuestAction {
  missionId: string;
  actionType: QuestActionType;
  notes?: string;
}

// 퀘스트 반복 조건 인터페이스
export interface IQuestRepeatSchedule {
  enabled: boolean;
  daysOfWeek: number[]; // 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토
}

export const defaultQuestRepeatSchedule: IQuestRepeatSchedule = {
  enabled: false,
  daysOfWeek: [1, 2, 3, 4, 5],
};

// 퀘스트 히스토리 액션 타입
export enum QuestHistoryAction {
  ACCEPT = 'accept', // 퀘스트 수락
  CANCEL = 'cancel', // 퀘스트 포기
  COMPLETE = 'complete', // 퀘스트 완료 (보상 수령)
  RESTART = 'restart', // 퀘스트 다시 시작
  ADMIN_FORCE_COMPLETE = 'admin_force_complete', // 관리자 강제 완료
  ADMIN_FORCE_CANCEL = 'admin_force_cancel', // 관리자 강제 취소(포기)
  ADMIN_FORCE_RESET = 'admin_force_reset', // 관리자 강제 진행 중으로 복원
  ADMIN_FORCE_ADD = 'admin_force_add', // 관리자 강제 퀘스트 추가
}

// 퀘스트 히스토리 로그 인터페이스
export interface IQuestHistory {
  id: string;
  questId: string; // 퀘스트 ID
  questTitle: string; // 퀘스트 제목
  action: QuestHistoryAction; // 수행한 액션
  characterId: string; // 캐릭터 ID
  characterName?: string; // 캐릭터 이름
  timestamp: Date; // 액션 수행 시간
  notes?: string; // 추가 메모 (보상 정보 등)
}
